import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { theme } from '../theme';
import { Sun, Moon, Smartphone } from 'lucide-react';
import { current, setMode } from '../colorMode.js';
// v0.171.0 — site mode switch (hold changes on this phone until Sync). The
// same setting the offline banner exposes; here so it is one tap from any
// screen. See utils/syncMode.js.
import { getSyncMode, setSyncMode, subscribeSyncMode } from '../utils/syncMode.js';

/**
 * UserMenu — v0.26.0 / v0.45.25.
 *
 * Top-right pill that shows the signed-in user's initial + name and
 * opens a small dropdown with account details + Sign out.
 *
 * v0.45.25 — Brett: "Dont understand how the subscriber will change
 * their details in admin? They dont have access." The previous menu
 * told subscribers their profile lived in InSpec Admin, which is
 * wrong: paying DropPin subscribers have NO InSpec access. Replaced
 * the InSpec pointer with a real /account self-edit link.
 */
export default function UserMenu({ user, onSignOut, version }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const [themeMode, setThemeMode] = useState(() => current());
  const dark = themeMode === 'dark';
  const toggleTheme = () => { const next = dark ? 'light' : 'dark'; setMode(next); setThemeMode(next); };
  const [syncMode, setSyncModeState] = useState(() => getSyncMode());
  useEffect(() => subscribeSyncMode(() => setSyncModeState(getSyncMode())), []);
  const hold = syncMode === 'hold';
  const toggleSiteMode = () => { const next = hold ? 'auto' : 'hold'; setSyncMode(next); setSyncModeState(next); };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!user) return null;
  const name = user.name || user.Username || 'Account';
  const initial = (name || '?').trim().charAt(0).toUpperCase();

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* v0.28.0 — pill lives on the dark navy header gradient, so the
          text/border use light colours. Previously inherited theme.text
          (near-black) on near-black → effectively invisible. */}
      <button
        onClick={() => setOpen(v => !v)}
        title={`Signed in as ${name}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 999, padding: '4px 10px 4px 4px',
          cursor: 'pointer', fontFamily: 'inherit', color: 'white',
        }}
      >
        <span style={{
          width: 26, height: 26, borderRadius: '50%',
          background: theme.brand, color: 'white',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700,
        }}>{initial}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'white' }}>{name.split(' ')[0]}</span>
        <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)',
          width: 260, background: theme.surface,
          border: `1px solid ${theme.border}`, borderRadius: 10,
          boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
          zIndex: 100, overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${theme.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                width: 36, height: 36, borderRadius: '50%',
                background: theme.brand, color: 'white',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, fontWeight: 700,
              }}>{initial}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                <div style={{ fontSize: 11, color: theme.textLight, overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.username || '—'}</div>
              </div>
            </div>
          </div>

          <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {user.email && <KV label="Email">{user.email}</KV>}
            {user.role && <KV label="Role">{user.role}</KV>}
            {user.company && <KV label="Company">{user.company}</KV>}
          </div>

          {/* v0.45.25 — Edit profile link replaces the misleading
              InSpec Admin pointer. Self-edit screen at /account. */}
          <button
            onClick={() => { setOpen(false); navigate('/account'); }}
            style={{
              width: '100%', textAlign: 'left', padding: '10px 14px',
              background: 'transparent', border: 'none',
              borderTop: `1px solid ${theme.border}`,
              color: theme.text, fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit', fontWeight: 600,
            }}
          >Edit profile →</button>

          <button
            onClick={toggleTheme}
            style={{
              width: '100%', textAlign: 'left', padding: '10px 14px',
              background: 'transparent', border: 'none',
              borderTop: `1px solid ${theme.border}`,
              color: theme.text, fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >{dark ? <Sun size={15} /> : <Moon size={15} />}{dark ? 'Light theme' : 'Dark theme'}</button>

          {/* v0.171.0 — site mode: hold pins + photos on this phone, sync when
              on Wi-Fi. Per device; persists. */}
          <button
            onClick={toggleSiteMode}
            title={hold ? 'On: changes stay on this phone until you tap Sync' : 'Off: changes send as you make them'}
            style={{
              width: '100%', textAlign: 'left', padding: '10px 14px',
              background: 'transparent', border: 'none',
              borderTop: `1px solid ${theme.border}`,
              color: theme.text, fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <Smartphone size={15} />
            <span>Site mode: {hold ? 'On' : 'Off'}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 500, color: hold ? theme.warn : theme.textLight }}>
              {hold ? 'hold until Sync' : 'send as I go'}
            </span>
          </button>

          <button
            onClick={() => { setOpen(false); onSignOut && onSignOut(); }}
            style={{
              width: '100%', textAlign: 'left', padding: '10px 14px',
              background: 'transparent', border: 'none',
              borderTop: `1px solid ${theme.border}`,
              color: theme.danger, fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit', fontWeight: 600,
            }}
          >Sign out</button>
          {version && (
            <div style={{ padding: '8px 14px', borderTop: `1px solid ${theme.border}`, fontSize: 11, color: theme.textLight, fontFamily: 'monospace' }}>
              DropPin v{version}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 6, fontSize: 12 }}>
      <div style={{ color: theme.textLight, fontWeight: 600 }}>{label}</div>
      <div style={{ color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</div>
    </div>
  );
}
